import { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Paper,
  Chip,
} from "@mui/material";
import { format } from "date-fns";
import useClientInvoice from "../../hooks/useClientInvoice";

const InvoiceAuditHistoryTab = ({ invoiceId }) => {
  const { getAuditHistory } = useClientInvoice();
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAuditHistory = async () => {
      setLoading(true);
      const history = await getAuditHistory(invoiceId);
      setAuditLog(history);
      setLoading(false);
    };

    if (invoiceId) {
      fetchAuditHistory();
    }
  }, [invoiceId, getAuditHistory]);

  const getActionColor = (action) => {
    switch (action) {
      case "created":
        return "info";
      case "approved":
        return "success";
      case "sent":
        return "primary";
      case "updated":
        return "default";
      case "deleted":
        return "error";
      case "line_added":
        return "success";
      case "line_updated":
        return "info";
      case "line_deleted":
        return "error";
      case "resent":
        return "secondary";
      default:
        return "default";
    }
  };

  const formatChangeValue = (value) => {
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "object") return JSON.stringify(value, null, 2);
    return String(value);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!auditLog || auditLog.length === 0) {
    return (
      <Box p={2}>
        <Typography color="textSecondary">
          No audit history available.
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ overflow: "auto" }}>
      <Table
        size="small"
        stickyHeader
      >
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
            <TableCell sx={{ fontWeight: "bold" }}>Timestamp</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>User</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Changes</TableCell>
            <TableCell sx={{ fontWeight: "bold" }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {auditLog.map((entry, idx) => (
            <TableRow
              key={idx}
              hover
            >
              <TableCell>
                <Typography variant="caption">
                  {format(new Date(entry.createdAt), "MMM dd, yyyy HH:mm:ss")}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  label={entry.action}
                  color={getActionColor(entry.action)}
                  size="small"
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {entry.userName || entry.userId}
                </Typography>
              </TableCell>
              <TableCell>
                {entry.changes ? (
                  <Box
                    sx={{
                      fontSize: "0.8rem",
                      fontFamily: "monospace",
                      maxWidth: 300,
                    }}
                  >
                    {Object.entries(entry.changes).map(([key, val]) => (
                      <Box
                        key={key}
                        mb={0.5}
                      >
                        <strong>{key}:</strong>
                        {typeof val === "object" && val.from !== undefined ? (
                          <Box
                            ml={1}
                            sx={{ backgroundColor: "#f0f0f0", p: 1 }}
                          >
                            <Box>From: {formatChangeValue(val.from)}</Box>
                            <Box>To: {formatChangeValue(val.to)}</Box>
                          </Box>
                        ) : (
                          <Box ml={1}>{formatChangeValue(val)}</Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                  >
                    No changes
                  </Typography>
                )}
              </TableCell>
              <TableCell>
                <Typography variant="caption">{entry.notes || "—"}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
};

export default InvoiceAuditHistoryTab;
